import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  Container,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import api from '../api/api';
import { ENDPOINTS } from '../api/endpoints';

const departments = [
  'Customer Support',
  'Advertising Dept.',
  'Casting Info',
  'Tech Help',
  'Billing'
];

const ContactForm = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    department: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!form.department) newErrors.department = 'Department is required';
    if (!form.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const params = {
        name: form.name,
        email: form.email,
        department: form.department,
        message: form.message
      };

      // Using the unified API wrapper
      const res = await api.post(ENDPOINTS.contactUs(params), '', {
        headers: { Accept: 'text/plain' }
      });

      if (res.status !== 200) throw new Error('Failed to send message');

      setToast({ open: true, message: 'Message sent successfully!', severity: 'success' });
      setForm({ name: '', email: '', department: '', message: '' });
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setToast({ open: true, message: err.message, severity: 'error' });
    }
  };

  return (
    <Container maxWidth="sm" sx={{ padding: '30px' }}>
      <Box component="form" onSubmit={handleSubmit} className="contactform">
        <Typography variant="h5" gutterBottom>
          Contact Us
        </Typography>

        <TextField
          label="Your Name"
          name="name"
          value={form.name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
          error={!!errors.name}
          helperText={errors.name}
        />

        <TextField
          label="Your Email"
          name="email"
          value={form.email}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
          error={!!errors.email}
          helperText={errors.email}
        />

        <FormControl fullWidth margin="normal" required error={!!errors.department}>
          <InputLabel>Department</InputLabel>
          <Select
            name="department"
            value={form.department}
            onChange={handleChange}
            label="Department"
          >
            <MenuItem value="">Select</MenuItem>
            {departments.map((dept) => (
              <MenuItem key={dept} value={dept}>
                {dept}
              </MenuItem>
            ))}
          </Select>
          {errors.department && (
            <Typography variant="caption" color="error">
              {errors.department}
            </Typography>
          )}
        </FormControl>

        <TextField
          label="Message"
          name="message"
          value={form.message}
          onChange={handleChange}
          fullWidth
          multiline
          rows={4}
          margin="normal"
          required
          error={!!errors.message}
          helperText={errors.message}
        />

        <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
          Submit
        </Button>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          sx={{ width: '100%' }}
          onClose={() => setToast({ ...toast, open: false })}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContactForm;
